import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLeads } from '../context/LeadContext';
import Button from '../components/ui/Button';

const FreeTrial = () => {
  const { addLead } = useLeads();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    website: '',
    locations: '1'
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    addLead(formData);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] py-20 bg-slate-50">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md p-10 text-center bg-white shadow-xl rounded-2xl"
        >
          <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
          </div>
          <h2 className="mb-4 text-3xl font-bold text-primary">Welcome Aboard!</h2>
          <p className="mb-8 text-lg text-slate-600">
            Your free trial has been activated. Check your email for login details and next steps.
          </p>
          <Button to="/" variant="primary" fullWidth>Return to Home</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-slate-50">
      <div className="container px-4 mx-auto max-w-6xl">
        <div className="flex flex-col gap-12 lg:flex-row">
          {/* Left Side - Info */}
          <div className="lg:w-1/2 lg:pr-12">
            <h1 className="mb-6 text-4xl font-bold md:text-5xl text-primary">Start your 14-day free trial.</h1>
            <p className="mb-8 text-xl text-slate-600">Join thousands of businesses already growing their reputation and revenue with Review Builder Pro.</p>
            
            <div className="space-y-6">
              {[
                "Full access to all features",
                "No credit card required",
                "Cancel anytime",
                "Setup takes less than 5 minutes"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary/20 text-secondary">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span className="font-medium text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="p-6 mt-12 border bg-blue-50 rounded-xl border-blue-100">
              <p className="italic font-medium text-blue-900">"We saw a 40% increase in positive Google reviews within our first two weeks of the trial."</p>
              <p className="mt-2 text-sm text-blue-700">— Sarah J., Local Cafe Owner</p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="lg:w-1/2">
            <div className="p-8 bg-white shadow-xl rounded-2xl">
              <h3 className="mb-6 text-2xl font-bold text-primary">Create Your Account</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">Full Name</label>
                    <input required type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border rounded-lg bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:bg-white transition-colors" placeholder="John Doe" />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">Business Name</label>
                    <input required type="text" name="business" value={formData.business} onChange={handleChange} className="w-full px-4 py-3 border rounded-lg bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:bg-white transition-colors" placeholder="Acme Corp" />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">Work Email</label>
                  <input required type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border rounded-lg bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:bg-white transition-colors" placeholder="john@acmecorp.com" />
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">Phone Number</label>
                    <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border rounded-lg bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:bg-white transition-colors" placeholder="(555) 000-0000" />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">Number of Locations</label>
                    <select name="locations" value={formData.locations} onChange={handleChange} className="w-full px-4 py-3 border rounded-lg bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:bg-white transition-colors">
                      <option value="1">1 Location</option>
                      <option value="2-5">2 - 5 Locations</option>
                      <option value="6-10">6 - 10 Locations</option>
                      <option value="11+">11+ Locations</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">Website URL (Optional)</label>
                  <input type="url" name="website" value={formData.website} onChange={handleChange} className="w-full px-4 py-3 border rounded-lg bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:bg-white transition-colors" placeholder="https://acmecorp.com" />
                </div>

                <div className="pt-4">
                  <Button type="submit" variant="primary" fullWidth size="lg">Start My Free Trial</Button>
                  <p className="mt-4 text-xs text-center text-slate-500">By clicking "Start My Free Trial", you agree to our Terms of Service and Privacy Policy.</p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeTrial;